// DELEGATE — the zone-setup write moved to `@heidi/evidence`; the app reactions
// stayed, and so did the cache identity behind one of them.
//
// The evidence-integrations key lives in evidence contracts; this delegate still invalidates it through the port.
//
// T2 rather than the `meta` tier, and not marginally: the error copy branches on
// HTTP STATUS across four cases with the backend's `detail.msg` substituted
// ahead of all of them, and one failure — `zone_not_supported`, which the setup
// dialog renders inline — produces no toast at all. `ToastCopyMeta` can express
// none of that, and the delegate could not have dissolved anyway with a foreign
// invalidation to fire.
//
// The exported surface is unchanged — the hook takes no arguments and returns
// the same mutation, and `isHealthPathwaysZoneNotSupportedError` is re-exported
// name-intact for the dialog that classifies the same rejection — so every call
// site and every `jest.mock('./useMutateSetupHealthPathways')`
// literal keeps resolving.
// LOAD-BEARING SIDE-EFFECT IMPORT — do not delete as "unused".
// The hub import warmed the Rematch model cluster; this specifier restores
// that init order. It retires with the Z2 codemod.

import { toast } from '@heidi/design-system/toast';
import { GET_EVIDENCE_INTEGRATIONS } from '@heidi/evidence/contracts';
import {
  isHealthPathwaysZoneNotSupportedError,
  useMutateSetupHealthPathways as useMutateSetupHealthPathwaysCore,
} from '@heidi/evidence/data';
import type { TranslationKey } from '@heidi/runtime/i18n';
import { useTranslations } from '@heidi/runtime/i18n';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

export { isHealthPathwaysZoneNotSupportedError };

export function useMutateSetupHealthPathways() {
  const queryClient = useQueryClient();
  const { t } = useTranslations();

  return useMutateSetupHealthPathwaysCore({
    onIntegrationsStale: () =>
      queryClient.invalidateQueries({
        queryKey: [GET_EVIDENCE_INTEGRATIONS],
      }),
    onSetupComplete: () => {
      toast({
        tone: 'success',
        title: t('integrationPartners.healthpathways.setupSuccess' as TranslationKey),
      });
    },
    onSetupError: (error) => {
      if (isHealthPathwaysZoneNotSupportedError(error)) return;

      let message = error.message;
      if (error instanceof AxiosError) {
        const status = error.response?.status;
        const detail = (error.response?.data as { detail?: { msg?: string } })?.detail
          ?.msg;
        if (detail) {
          message = detail;
        } else if (status === 400) {
          message = t(
            'integrationPartners.healthpathways.errorInvalidInput' as TranslationKey
          );
        } else if (status === 502) {
          message = t(
            'integrationPartners.healthpathways.errorPostcodeNotFound' as TranslationKey
          );
        } else if (status === 503) {
          message = t(
            'integrationPartners.healthpathways.errorServiceUnavailable' as TranslationKey
          );
        }
      }
      toast({
        tone: 'destructive',
        title: t('integrationPartners.healthpathways.setupError' as TranslationKey),
        description: message,
      });
    },
  });
}
